import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, Sparkles, Shield, MessageCircle, Gift, Play, Globe } from "lucide-react";
import { useAppMode } from "@/context/AppModeContext";
import PromoVideoDialog from "@/components/PromoVideoDialog";
import { MsDLuxeVideoGrid } from "@/components/MsDLuxeVideos";
import { useLocale } from "@/i18n/LocaleContext";
import { LANGS } from "@/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DLUXE_HERO_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_e1250066-ed24-4faf-8451-7a13993519fa/artifacts/mtqhmiqd_muscular_back_tattoo_13.webp";

export default function Landing() {
  const navigate = useNavigate();
  const { appName, otherAppName, isDluxe, toggleMode } = useAppMode();
  const { t, lang, setLang } = useLocale();
  const [showPromo, setShowPromo] = useState(false);
  const [promoSound, setPromoSound] = useState(false);
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get("ref");
  const registerPath = refParam ? `/register?ref=${refParam}` : "/register";

  useEffect(() => {
    if (searchParams.get("film")) setShowPromo(true);
  }, [searchParams]);

  const handleHeroBackdropClick = (e) => {
    if (e.target.closest("button, a, input, textarea, [role='dialog'], [role='menu'], [role='menuitem'], [data-radix-popper-content-wrapper]")) return;
    if (e.clientY + window.scrollY > window.innerHeight * 0.85) return;
    setPromoSound(true);
    setShowPromo(true);
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" onClick={handleHeroBackdropClick}>
      {/* Background gradient effects + film teaser */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-x-0 top-0 h-[90vh] pointer-events-auto cursor-pointer"
          data-testid="landing-film-teaser"
          title="Watch the film with sound"
          onClick={() => {
            setPromoSound(true);
            setShowPromo(true);
          }}
        >
          <video
            src={`${process.env.REACT_APP_BACKEND_URL}/api/media/promo.mp4#t=4`}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover opacity-[0.18]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/35 via-background/50 to-background" />
        </div>
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-4 py-4 md:px-12 pointer-events-none">
        <div className="flex items-center gap-2.5 pointer-events-auto">
          <button
            type="button"
            onClick={toggleMode}
            title={`Switch to ${otherAppName}`}
            className={`flex items-center justify-center w-9 h-9 rounded-full border transition-transform hover:scale-110 shrink-0 ${
              isDluxe ? "bg-violet-500/15 border-violet-500/40" : "bg-rose-600/15 border-rose-600/40"
            }`}
            data-testid="landing-app-switch-btn"
          >
            <Heart className={`w-4 h-4 heart-pulse ${isDluxe ? "text-violet-400 fill-violet-400" : "text-rose-500 fill-rose-500"}`} />
          </button>
          {isDluxe ? (
            <img src="/logo-dluxe.png" alt="DLuxe-meet.com" className="h-7 sm:h-9 w-auto" data-testid="landing-dluxe-logo" />
          ) : (
            <>
              <Heart className="w-8 h-8 text-primary fill-primary" />
              <span className="text-xl font-bold font-['Outfit'] tracking-tight" data-testid="landing-app-name">{appName}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0 pointer-events-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-white" data-testid="language-switcher-btn">
                <Globe className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-white/10">
              {Object.entries(LANGS).map(([code, label]) => (
                <DropdownMenuItem
                  key={code}
                  onClick={() => setLang(code)}
                  className={lang === code ? "text-primary font-semibold" : ""}
                  data-testid={`lang-option-${code}`}
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button 
            variant="ghost" 
            className="text-muted-foreground hover:text-white px-2 sm:px-4"
            onClick={() => navigate("/login")}
            data-testid="nav-login-btn"
          >
            {t("auth.signIn")}
          </Button>
          <Button 
            className="bg-primary hover:bg-primary/90 rounded-full px-4 sm:px-6"
            onClick={() => navigate(registerPath)}
            data-testid="nav-register-btn"
          >
            {t("auth.getStarted")}
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 px-6 md:px-12 pt-16 pb-24 pointer-events-none">
        <div
          className="max-w-6xl mx-auto pointer-events-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setPromoSound(true);
              setShowPromo(true);
            }
          }}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left column - Text */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm text-primary font-medium">{t("landing.badge")}</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold font-['Outfit'] tracking-tighter leading-tight">
                {isDluxe ? t("landing.heroDluxe1") : t("landing.heroLuxe1")}
                <span className="block gradient-neon bg-clip-text text-transparent">
                  {isDluxe ? t("landing.heroDluxe2") : t("landing.heroLuxe2")}
                </span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                {isDluxe ? t("landing.taglineDluxe") : t("landing.taglineLuxe")}
              </p>

              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-semibold glow-primary btn-scale transition-all"
                  onClick={() => navigate(registerPath)}
                  data-testid="hero-get-started-btn"
                >
                  {t("landing.startJourney")}
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg border-white/10 hover:bg-white/5"
                  onClick={() => navigate("/login")}
                  data-testid="hero-login-btn"
                >
                  {t("landing.haveAccount")}
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  className="rounded-full px-8 py-6 text-lg text-muted-foreground hover:text-white hover:bg-white/5"
                  onClick={() => {
                    setPromoSound(false);
                    setShowPromo(true);
                  }}
                  data-testid="hero-watch-video-btn"
                >
                  <Play className="w-5 h-5 mr-2 fill-current" /> {t("landing.watchFilm")}
                </Button>
              </div>
              <div className="space-y-1.5 text-sm text-muted-foreground max-w-md">
                <p data-testid="free-credits-promo">💌 {t("landing.freeCreditsPromo")}</p>
                <p data-testid="lang-highlight">🌍 {t("landing.langHighlight")}</p>
              </div>
              <PromoVideoDialog open={showPromo} onOpenChange={setShowPromo} withSound={promoSound} />
            </div>

            {/* Right column - preview */}
            {isDluxe ? (
              <div className="relative flex flex-col justify-center mt-12 lg:mt-0 lg:h-[600px]">
                <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                  <img
                    src={DLUXE_HERO_IMG}
                    alt="DLuxe"
                    className="w-full h-auto object-cover"
                    data-testid="dluxe-hero-image"
                  />
                </div>

                <div className="absolute top-2 right-0 lg:top-10 glass-card p-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full gradient-gold flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t("land.newMatch")}</p>
                      <p className="text-xs text-muted-foreground">{t("land.firstMove")}</p>
                    </div>
                  </div>
                </div>

                <div className="glass-card p-4 mt-4 self-start animate-fade-in" style={{ animationDelay: "0.4s" }} data-testid="dluxe-terms-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t("land.dateTerms")}</p>
                      <p className="text-xs text-muted-foreground">{t("land.youChoose")}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative block mt-12 lg:mt-0">
                <div className="relative w-full h-[560px] max-w-[360px] mx-auto lg:max-w-none lg:mx-0 lg:h-[600px]">
                  {/* Background card */}
                  <div className="absolute top-8 left-8 w-72 h-96 rounded-3xl overflow-hidden border border-white/10 opacity-60 transform rotate-6">
                    <img 
                      src="https://images.pexels.com/photos/4694514/pexels-photo-4694514.jpeg?auto=compress&cs=tinysrgb&w=600" 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Main card */}
                  <div className="absolute top-0 left-0 w-72 h-96 rounded-3xl overflow-hidden border border-white/10 shadow-2xl transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/97d20d91-0b8c-4ea0-a519-7505d3bf2363/images/041bbab09a82b0e6cb897090b866b3710c3ec4aa11ca005d711c577188fbc160.jpeg" 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 profile-overlay" />
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="text-2xl font-bold font-['Outfit']">Sophia, 26</h3>
                      <p className="text-white/70 text-sm">New York City</p>
                    </div>
                  </div>

                  {/* Floating elements */}
                  <div className="absolute top-2 right-0 lg:top-20 glass-card p-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full gradient-gold flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-black" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{t("land.newMatch")}</p>
                        <p className="text-xs text-muted-foreground">{t("land.startChatting")}</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute bottom-32 right-8 glass-card p-4 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Heart className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{t("land.likes", { n: 256 })}</p>
                        <p className="text-xs text-muted-foreground">{t("land.thisWeek")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Meet Ms DLuxe — explainer videos */}
        <div className="max-w-4xl mx-auto mt-20" data-testid="msdluxe-section">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-500/10 border border-rose-500/20 mb-5">
              <span className="text-sm text-rose-300 font-medium">{t("msdluxe.badge")}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              {t("msdluxe.title1")}
              <span className="block bg-gradient-to-r from-rose-300 via-rose-400 to-pink-500 bg-clip-text text-transparent pb-1">{t("msdluxe.title2")}</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed">{t("msdluxe.desc")}</p>
          </div>
          <MsDLuxeVideoGrid idPrefix="landing-msdluxe" />
          <p className="text-xs text-muted-foreground text-center mt-4">{t("msdluxe.share")}</p>
        </div>

        {/* Features Section */}
        <div className="max-w-6xl mx-auto mt-32">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-4">
              {t("landing.whyTitle").replace("{app}", appName)}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("landing.whySub")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass-card p-8 hover:border-primary/30 transition-colors">
              <div className="w-14 h-14 rounded-2xl gradient-neon flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold font-['Outfit'] mb-3">{t("landing.f1Title")}</h3>
              <p className="text-muted-foreground leading-relaxed">
                {t("landing.f1Desc")}
              </p>
            </div>

            <div className="glass-card p-8 hover:border-secondary/30 transition-colors">
              <div className="w-14 h-14 rounded-2xl gradient-gold flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-black" />
              </div>
              <h3 className="text-xl font-semibold font-['Outfit'] mb-3">{t("landing.f2Title")}</h3>
              <p className="text-muted-foreground leading-relaxed">
                {isDluxe ? t("landing.f2DescDluxe") : t("landing.f2DescLuxe")}
              </p>
            </div>

            <div className="glass-card p-8 hover:border-accent/30 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-accent/20 flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-accent" />
              </div>
              <h3 className="text-xl font-semibold font-['Outfit'] mb-3">{t("landing.f3Title")}</h3>
              <p className="text-muted-foreground leading-relaxed">
                {t("landing.f3Desc")}
              </p>
            </div>
          </div>
        </div>

        {/* Go Official Section */}
        <div className="max-w-4xl mx-auto mt-24" data-testid="go-official-section">
          <div className="glass-card p-10 md:p-12 relative overflow-hidden border border-rose-500/20">
            <div className="absolute -top-24 -left-24 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10 text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-500/10 border border-rose-500/20 mb-5">
                <Heart className="w-4 h-4 text-rose-400 fill-rose-400" />
                <span className="text-sm text-rose-300 font-medium">{t("landing.goBadge")}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
                {t("landing.goTitle1")}
                <span className="block bg-gradient-to-r from-rose-300 via-rose-400 to-pink-500 bg-clip-text text-transparent pb-1">{t("landing.goTitle2")}</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t("landing.goDesc")}
              </p>
              <div className="flex flex-wrap justify-center gap-3 text-sm">
                <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">{t("landing.goChip1")}</span>
                <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">{t("landing.goChip2")}</span>
                <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">{t("landing.goChip3")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Refer & Earn Section */}
        <div className="max-w-4xl mx-auto mt-32" data-testid="refer-earn-section">
          <div className="glass-card p-10 md:p-12 relative overflow-hidden border border-secondary/20">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-secondary/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-5">
                  <Gift className="w-4 h-4 text-secondary" />
                  <span className="text-sm text-secondary font-medium">{t("landing.refBadge")}</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
                  {t("landing.refTitle1")}
                  <span className="block bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent pb-1">{t("landing.refTitle2")}</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t("landing.refDesc1")}{" "}
                  <span className="text-secondary font-semibold">{t("landing.refDescBold")}</span>{" "}
                  {t("landing.refDesc2")}
                </p>
              </div>
              <div className="space-y-4">
                <div className="glass-card p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full gradient-gold flex items-center justify-center shrink-0">
                    <Gift className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <p className="font-semibold">{t("landing.refCard1Title")}</p>
                    <p className="text-sm text-muted-foreground">{t("landing.refCard1Desc")}</p>
                  </div>
                </div>
                <div className="glass-card p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">{t("landing.refCard2Title")}</p>
                    <p className="text-sm text-muted-foreground">{t("landing.refCard2Desc")}</p>
                  </div>
                </div>
                <Button
                  className="w-full h-12 gradient-gold text-black font-semibold rounded-xl btn-scale"
                  onClick={() => navigate(registerPath)}
                  data-testid="refer-earn-cta-btn"
                >
                  {t("landing.refCta")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto mt-32 text-center">
          <div className="glass-card p-12 relative overflow-hidden">
            <div className="absolute inset-0 gradient-neon opacity-10" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
                {t("landing.ctaTitle")}
              </h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                {t("landing.ctaSub").replace("{app}", appName)}
              </p>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-semibold glow-primary btn-scale"
                onClick={() => navigate(registerPath)}
                data-testid="cta-register-btn"
              >
                {t("landing.ctaBtn")}
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8 px-6 md:px-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {isDluxe ? (
              <img src="/logo-dluxe.png" alt="DLuxe-meet.com" className="h-6 w-auto" />
            ) : (
              <>
                <Heart className="w-5 h-5 text-primary fill-primary" />
                <span className="font-semibold font-['Outfit']">{appName}</span>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <a href="mailto:contact@luxe-meet.com" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="footer-contact-email">
              contact@luxe-meet.com
            </a>
            <a href="tel:+17343302395" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="footer-contact-phone">
              (734) 330-2395
            </a>
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="footer-terms-link">
              {t("landing.footerTerms")}
            </Link>
            <p className="text-sm text-muted-foreground">
              © 2026 {appName}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
